import { useContext, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useLocation } from "react-router";
import { useQueryClient } from "react-query";

import { BasePage } from "components/BasePage/BasePage";
import { SubscriptionStatusBanner } from "components/Billing/SubscriptionStatusBanner";
import { PlanSelector } from "components/Billing/PlanSelector";
import { PrimaryButton } from "components/Button/PrimaryButton";
import {
  useSubscription,
  useCancelSubscription,
  useOpenBillingPortal,
} from "hooks/useStripe";
import { ControlContext } from "context/Controls";
import { useConfirmation } from "context/Confirmation";

export const BillingPage = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { displayNotification } = useContext(ControlContext);
  const { openConfirmation } = useConfirmation();

  const { subscription, isLoading, uiState } = useSubscription();
  const { cancelSubscription, isLoading: isCancelling } =
    useCancelSubscription();
  const { openBillingPortal, isLoading: isOpeningPortal } =
    useOpenBillingPortal();

  useEffect(() => {
    const params = new URLSearchParams(location.search);

    if (params.get("success") === "true") {
      queryClient.invalidateQueries(["stripe", "subscription"]);
      displayNotification!(t("settings.billing.checkout_success"), "", "success");
      history.replace("/settings/billing");
    } else if (params.get("canceled") === "true") {
      displayNotification!(t("settings.billing.checkout_canceled"), "", "error");
      history.replace("/settings/billing");
    }
  }, [location.search, displayNotification, history, queryClient, t]);

  const handleCancel = () => {
    openConfirmation(() => cancelSubscription(), {
      label: t("settings.billing.cancel_confirm_title"),
      subline: t("settings.billing.cancel_confirm_message"),
    });
  };

  return (
    <BasePage
      breadcrumbItems={[
        { label: t("settings.tab.billing"), location: "/settings" },
        {
          label: t("settings.tab.billing"),
          location: "/settings/billing",
        },
      ]}
      loading={isLoading}
    >
      <div className="space-y-10">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">
            {t("settings.billing.title")}
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            {t("settings.billing.page_subtitle")}
          </p>
        </div>

        <SubscriptionStatusBanner subscription={subscription} />

        {(uiState === "free" || uiState === "trial_active") && (
          <>
            <div className="border-t border-gray-200 pt-8">
              <h3 className="text-lg font-semibold text-gray-900">
                {t("settings.billing.choose_plan")}
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                {t("settings.billing.choose_plan_subtitle")}
              </p>
            </div>
            <PlanSelector
              currentPlan={subscription?.plan}
              currentFrequency={subscription?.frequency}
            />
          </>
        )}

        {(uiState === "active" || uiState === "cancelling") && (
          <div className="border-t border-gray-200 pt-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-1">
              {t("settings.billing.manage_heading")}
            </h3>
            <p className="text-sm text-gray-500 mb-5">
              {t("settings.billing.manage_subtitle")}
            </p>
            <div className="flex space-x-3">
              <PrimaryButton
                disabled={isOpeningPortal}
                onClick={() => openBillingPortal()}
              >
                {t("settings.billing.action_manage")}
              </PrimaryButton>
              {uiState === "active" && (
                <button
                  disabled={isCancelling}
                  onClick={handleCancel}
                  className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 disabled:opacity-50"
                >
                  {t("settings.billing.action_cancel")}
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </BasePage>
  );
};

export default BillingPage;
